/**
 * PathReport (FEAT-ANALYTICS) — user-flow Sankey: step-by-step transitions
 * between events (or pages) within a session. Free, data-driven flow of what
 * users actually do next — distinct from the fixed Purchase/Checkout journeys
 * and the dedicated Funnels builder. Data: /analytics/path.
 */
import React, { useMemo, useState } from 'react';
import { Box, Flex, Text, Select, HStack } from '@chakra-ui/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HCSankey from 'highcharts/modules/sankey';
import { useReport, useDimensionValues } from './api';
import { ReportFrame, EmptyState } from './components';

if (typeof HCSankey === 'function') { try { HCSankey(Highcharts); } catch (_) { /* already registered */ } }

const STEP_COLORS = ['#2563eb', '#0891b2', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed', '#db2777', '#475569'];
const DEPTHS = [3, 4, 5, 6];

function Sankey({ nodes, links }) {
  const options = useMemo(() => ({
    // Taller canvas so the many thin (low-traffic) nodes get vertical room for a
    // legible label instead of colliding with their neighbours.
    chart: { type: 'sankey', height: 620, style: { fontFamily: 'inherit' }, backgroundColor: 'transparent' },
    title: { text: undefined },
    credits: { enabled: false },
    accessibility: { enabled: false },
    tooltip: { headerFormat: '', nodeFormat: '{point.name}: <b>{point.sum}</b> sessions', pointFormat: '{point.fromNode.name} → {point.toNode.name}: <b>{point.weight}</b> sessions' },
    series: [{
      type: 'sankey',
      keys: ['from', 'to', 'weight'],
      data: (links || []).map((l) => [l.source, l.target, l.value]),
      nodes: (nodes || []).map((n) => ({ id: n.id, name: n.label, color: STEP_COLORS[n.step % STEP_COLORS.length] })),
      // Bars wide enough for the full event name to sit INSIDE the bar (even the
      // longest, e.g. "category_selection"), so a label on a THIN node no longer
      // spills sideways over the link ribbons where it was unreadable.
      nodeWidth: 108,
      dataLabels: {
        enabled: true,
        align: 'center',
        verticalAlign: 'middle',
        nodeFormat: '{point.name}',
        crop: false,
        overflow: 'allow',
        allowOverlap: true, // keep every node labelled; never silently drop one
        padding: 1,
        // white label centered INSIDE the wide node bar
        style: { fontSize: '10px', fontWeight: '700', textOutline: 'none', color: '#ffffff' },
      },
      nodePadding: 16, // more vertical gap so thin nodes' labels don't collide
      linkOpacity: 0.38,
    }],
  }), [nodes, links]);
  return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export function PathReport({ params }) {
  const [dimension, setDimension] = useState('event');
  const [depth, setDepth] = useState(4);
  const [start, setStart] = useState('');
  const { values: events } = useDimensionValues('event');

  const q = useMemo(() => ({ ...params, dimension, depth, ...(start ? { start } : {}) }), [params, dimension, depth, start]);
  const { data, loading, error } = useReport('path', q);
  const hasFlow = (data?.links || []).length > 0;

  return (
    <Box>
      <Flex mb={4} align="center" gap={3} wrap="wrap">
        <HStack>
          <Text fontSize="sm" color="gray.600">Flow of</Text>
          <Select size="sm" w="auto" bg="white" value={dimension} onChange={(e) => { setDimension(e.target.value); setStart(''); }}>
            <option value="event">Events</option>
            <option value="page">Pages / screens</option>
          </Select>
        </HStack>
        <HStack>
          <Text fontSize="sm" color="gray.600">Steps</Text>
          <Select size="sm" w="auto" bg="white" value={depth} onChange={(e) => setDepth(Number(e.target.value))}>
            {DEPTHS.map((d) => <option key={d} value={d}>{d}</option>)}
          </Select>
        </HStack>
        {dimension === 'event' && events.length > 0 && (
          <HStack>
            <Text fontSize="sm" color="gray.600">Start</Text>
            <Select size="sm" w="auto" minW="170px" bg="white" value={start} placeholder="any event" onChange={(e) => setStart(e.target.value)}>
              <option value="">any event</option>
              {events.map((v) => <option key={v} value={v}>{v}</option>)}
            </Select>
          </HStack>
        )}
      </Flex>

      <ReportFrame loading={loading} error={error}>
        {data && (
          <Box bg="white" borderWidth="1px" borderColor="#ebedf0" borderRadius="14px" p={{ base: 3, md: 5 }}>
            <Text fontSize="xs" color="gray.500" mb={2}>
              Left-to-right: where sessions go next. Each column is a step; link width = sessions. Top 8 per step, the rest grouped as “(other)”.
            </Text>
            {hasFlow ? <Sankey nodes={data.nodes} links={data.links} /> : <EmptyState label="Not enough multi-step sessions to draw a flow" />}
          </Box>
        )}
      </ReportFrame>
    </Box>
  );
}

export default PathReport;
